import { useState, useCallback, useRef } from 'react';

type SlideDirection = '' | 'animate-slide-in-right' | 'animate-slide-in-left';

/**
 * Wraps a page change callback to track slide direction for animation.
 * Returns a wrapped onPageChange and a CSS animation class to apply to the table body.
 * The animation class is cleared after the animation completes (~200ms).
 */
export function usePageTransition(onPageChange: (page: number) => void, currentPage: number) {
  const [slideClass, setSlideClass] = useState<SlideDirection>('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handlePageChange = useCallback((newPage: number) => {
    if (newPage === currentPage) return;

    // Clear any pending timer
    if (timerRef.current) clearTimeout(timerRef.current);

    // Set animation direction: new content slides in from the direction of navigation
    setSlideClass(newPage > currentPage ? 'animate-slide-in-right' : 'animate-slide-in-left');

    onPageChange(newPage);

    // Clear after animation completes
    timerRef.current = setTimeout(() => setSlideClass(''), 250);
  }, [onPageChange, currentPage]);

  return { handlePageChange, slideClass };
}
